import React, { useState } from 'react';
import {
  FileText, Download, Upload, Star, Clock, Copy, Trash2,
  Search, Grid, List, Package, AlertCircle, Check,
  Tag, User, Calendar, TrendingUp, Heart,
  Lock, Share2, Unlock
} from 'lucide-react';

export interface FlowTemplate {
  id: string;
  name: string;
  description: string;
  category: 'trigger' | 'integration' | 'data' | 'notification' | 'workflow' | 'custom';
  tags: string[];
  thumbnail?: string;
  flowData: any; // ReactFlow data
  createdAt: Date;
  updatedAt: Date;
  author?: string;
  version: string;
  isPublic: boolean;
  isFavorite?: boolean;
  usageCount: number;
  rating?: number;
  dependencies?: string[];
}

interface TemplateLibraryProps {
  templates: FlowTemplate[];
  onTemplateSelect?: (template: FlowTemplate) => void;
  onTemplateImport?: (file: File) => void;
  onTemplateExport?: (template: FlowTemplate) => void;
  onTemplateCreate?: (flowData: any, metadata: Partial<FlowTemplate>) => void;
  onTemplateDelete?: (id: string) => void;
  onTemplateFavorite?: (id: string, isFavorite: boolean) => void;
  onTemplateShare?: (id: string) => void;
  currentFlowData?: any;
  isReadOnly?: boolean;
}

export const TemplateLibrary: React.FC<TemplateLibraryProps> = ({
  templates = [],
  onTemplateSelect,
  onTemplateImport,
  onTemplateExport,
  onTemplateCreate,
  onTemplateDelete,
  onTemplateFavorite,
  onTemplateShare,
  currentFlowData,
  isReadOnly = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<FlowTemplate | null>(null);
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'usage'>('date');
  const [newTemplate, setNewTemplate] = useState<Partial<FlowTemplate>>({
    name: '',
    description: '',
    category: 'workflow',
    tags: [],
    isPublic: false
  });

  const categories = [
    { id: 'all', label: 'Todos', icon: <Package size={14} /> },
    { id: 'trigger', label: 'Gatilhos', icon: <AlertCircle size={14} />, color: '#EF4444' },
    { id: 'integration', label: 'Integrações', icon: <Share2 size={14} />, color: '#3B82F6' },
    { id: 'data', label: 'Dados', icon: <FileText size={14} />, color: '#10B981' },
    { id: 'notification', label: 'Notificações', icon: <AlertCircle size={14} />, color: '#F59E0B' },
    { id: 'workflow', label: 'Workflows', icon: <TrendingUp size={14} />, color: '#8B5CF6' },
    { id: 'custom', label: 'Personalizados', icon: <Star size={14} />, color: '#EC4899' }
  ];

  const getCategoryColor = (category: string) => {
    const cat = categories.find(c => c.id === category);
    return cat?.color || '#6B7280';
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = !searchQuery || 
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'usage':
        return b.usageCount - a.usageCount;
      case 'date':
      default:
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    }
  });

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onTemplateImport) {
      onTemplateImport(file);
    }
  };

  const handleCreateTemplate = () => {
    if (currentFlowData && onTemplateCreate && newTemplate.name) {
      onTemplateCreate(currentFlowData, {
        ...newTemplate,
        tags: newTemplate.tags || [],
        version: '1.0.0',
        createdAt: new Date(),
        updatedAt: new Date(),
        usageCount: 0
      });
      setShowCreateModal(false);
      setNewTemplate({
        name: '',
        description: '',
        category: 'workflow',
        tags: [],
        isPublic: false
      });
    }
  };

  const handleAddTag = (tag: string) => {
    if (tag && !newTemplate.tags?.includes(tag)) {
      setNewTemplate({
        ...newTemplate,
        tags: [...(newTemplate.tags || []), tag]
      });
    }
  };

  const handleRemoveTag = (tag: string) => {
    setNewTemplate({
      ...newTemplate,
      tags: newTemplate.tags?.filter(t => t !== tag) || []
    });
  };

  const renderTemplateCard = (template: FlowTemplate) => (
    <div
      key={template.id}
      onClick={() => setSelectedTemplate(template)}
      style={{
        background: '#f9fafb',
        border: `1px solid ${selectedTemplate?.id === template.id ? '#3B82F6' : '#d1d5db'}`,
        borderRadius: '8px',
        padding: '12px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = '#3B82F6';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = selectedTemplate?.id === template.id ? '#3B82F6' : '#d1d5db';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '8px'
      }}>
        <div style={{ flex: 1 }}>
          <h4 style={{
            margin: '0 0 4px 0',
            fontSize: '13px',
            fontWeight: 600,
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            {template.name}
            {template.isPublic ? <Unlock size={12} color="#10B981" /> : <Lock size={12} color="#6B7280" />}
            {template.isFavorite && <Heart size={12} color="#EF4444" fill="#EF4444" />}
          </h4>
          <p style={{
            margin: 0,
            fontSize: '11px',
            color: '#6b7280',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {template.description}
          </p>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <span style={{
            fontSize: '9px',
            color: '#ffffff',
            background: getCategoryColor(template.category),
            padding: '2px 6px',
            borderRadius: '10px',
            textAlign: 'center'
          }}>
            {template.category}
          </span>
          {template.rating && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              fontSize: '10px',
              color: '#F59E0B'
            }}>
              <Star size={10} fill="#F59E0B" />
              {template.rating.toFixed(1)}
            </div>
          )}
        </div>
      </div>

      {/* Tags */}
      {template.tags.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
          marginBottom: '8px'
        }}>
          {template.tags.slice(0, 3).map(tag => (
            <span
              key={tag}
              style={{
                fontSize: '9px',
                color: '#6b7280',
                background: '#ffffff',
                padding: '2px 6px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '2px'
              }}
            >
              <Tag size={8} />
              {tag}
            </span>
          ))}
          {template.tags.length > 3 && (
            <span style={{
              fontSize: '9px',
              color: '#64748b',
              padding: '2px 6px'
            }}>
              +{template.tags.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: '8px',
        borderTop: '1px solid #e5e7eb',
        fontSize: '10px',
        color: '#64748b'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <Clock size={10} />
            {new Date(template.updatedAt).toLocaleDateString('pt-BR', { 
              day: '2-digit',
              month: '2-digit'
            })}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <TrendingUp size={10} />
            {template.usageCount} usos
          </span>
        </div>
        {template.author && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <User size={10} />
            {template.author}
          </span>
        )}
      </div>

      {/* Actions */}
      {viewMode === 'grid' && (
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          display: 'flex',
          gap: '4px',
          opacity: 0,
          transition: 'opacity 0.3s ease'
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = '1'}
        onMouseLeave={e => e.currentTarget.style.opacity = '0'}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTemplateSelect?.(template);
            }}
            style={{
              padding: '4px',
              background: '#3B82F6',
              border: 'none',
              borderRadius: '4px',
              color: '#ffffff',
              cursor: 'pointer'
            }}
          >
            <Check size={12} />
          </button>
          {onTemplateExport && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTemplateExport(template);
              }}
              style={{
                padding: '4px',
                background: '#10B981',
                border: 'none',
                borderRadius: '4px',
                color: '#ffffff',
                cursor: 'pointer'
              }}
            >
              <Download size={12} />
            </button>
          )}
          {!isReadOnly && onTemplateDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTemplateDelete(template.id);
              }}
              style={{
                padding: '4px',
                background: '#EF4444',
                border: 'none',
                borderRadius: '4px',
                color: '#ffffff',
                cursor: 'pointer'
              }}
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      )}
    </div>
  );

  const renderTemplateList = (template: FlowTemplate) => (
    <div
      key={template.id}
      onClick={() => setSelectedTemplate(template)}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '10px',
        background: selectedTemplate?.id === template.id ? '#f1f5f9' : '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        cursor: 'pointer',
        transition: 'background 0.2s ease'
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
      onMouseLeave={e => e.currentTarget.style.background = selectedTemplate?.id === template.id ? '#f1f5f9' : '#ffffff'}
    >
      <div style={{ flex: 1 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '4px'
        }}>
          <h4 style={{
            margin: 0,
            fontSize: '12px',
            fontWeight: 600,
            color: '#374151'
          }}>
            {template.name}
          </h4>
          <span style={{
            fontSize: '9px',
            color: '#ffffff',
            background: getCategoryColor(template.category),
            padding: '2px 6px',
            borderRadius: '10px'
          }}>
            {template.category}
          </span>
          {template.isPublic && <Unlock size={12} color="#10B981" />}
          {template.isFavorite && <Heart size={12} color="#EF4444" fill="#EF4444" />}
        </div>
        <p style={{
          margin: 0,
          fontSize: '11px',
          color: '#6b7280'
        }}>
          {template.description}
        </p>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginTop: '4px',
          fontSize: '10px',
          color: '#64748b'
        }}>
          <span>{new Date(template.updatedAt).toLocaleDateString('pt-BR')}</span>
          <span>{template.usageCount} usos</span>
          {template.author && <span>por {template.author}</span>}
        </div>
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onTemplateSelect?.(template);
          }}
          style={{
            padding: '6px 10px',
            background: '#3B82F6',
            border: 'none',
            borderRadius: '4px',
            color: '#ffffff',
            fontSize: '11px',
            cursor: 'pointer'
          }}
        >
          Usar
        </button>
        {onTemplateExport && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTemplateExport(template);
            }}
            style={{
              padding: '6px',
              background: '#10B981',
              border: 'none',
              borderRadius: '4px',
              color: '#ffffff',
              cursor: 'pointer'
            }}
          >
            <Download size={12} />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#ffffff',
      color: '#374151'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #d1d5db',
        background: '#f9fafb'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '14px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Package size={16} />
            Biblioteca de Templates
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            {currentFlowData && !isReadOnly && (
              <button
                onClick={() => setShowCreateModal(true)}
                style={{
                  padding: '6px 12px',
                  background: '#8B5CF6',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Copy size={14} />
                Salvar como Template
              </button>
            )}
            {onTemplateImport && (
              <label style={{
                padding: '6px 12px',
                background: '#3B82F6',
                border: 'none',
                borderRadius: '6px',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <Upload size={14} />
                Importar
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileImport}
                  style={{ display: 'none' }}
                />
              </label>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={14} style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#64748b'
            }} />
            <input
              type="text"
              placeholder="Buscar templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 6px 6px 32px',
                background: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                color: '#374151',
                fontSize: '12px',
                outline: 'none'
              }}
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            style={{
              padding: '6px',
              background: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              color: '#374151',
              fontSize: '12px',
              outline: 'none'
            }}
          >
            <option value="date">Mais Recentes</option>
            <option value="name">Nome</option>
            <option value="usage">Mais Usados</option>
          </select>
          <button
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            style={{
              padding: '6px',
              background: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              color: '#374151',
              cursor: 'pointer'
            }}
          >
            {viewMode === 'grid' ? <List size={14} /> : <Grid size={14} />}
          </button>
        </div>

        {/* Categories */}
        <div style={{
          display: 'flex',
          gap: '6px',
          overflowX: 'auto',
          padding: '4px 0'
        }}>
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              style={{
                padding: '4px 10px',
                background: selectedCategory === category.id ? '#3B82F6' : '#f1f5f9',
                border: `1px solid ${selectedCategory === category.id ? '#3B82F6' : '#d1d5db'}`,
                borderRadius: '12px',
                color: selectedCategory === category.id ? '#ffffff' : '#374151',
                fontSize: '11px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap'
              }}
            >
              {category.icon}
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '12px'
      }}>
        {filteredTemplates.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '200px',
            color: '#64748b'
          }}>
            <Package size={40} style={{ marginBottom: '12px' }} />
            <p style={{ margin: 0, fontSize: '12px' }}>
              Nenhum template encontrado
            </p>
          </div>
        ) : (
          viewMode === 'grid' ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '12px'
            }}>
              {filteredTemplates.map(renderTemplateCard)}
            </div>
          ) : (
            <div>
              {filteredTemplates.map(renderTemplateList)}
            </div>
          )
        )}
      </div>

      {/* Create Template Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '500px',
            maxWidth: '90%',
            background: '#f9fafb',
            borderRadius: '12px',
            padding: '24px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '16px',
              fontWeight: 600,
              color: '#374151'
            }}>
              Salvar Fluxo como Template
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  color: '#6b7280',
                  marginBottom: '4px'
                }}>
                  Nome do Template *
                </label>
                <input
                  type="text"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    color: '#374151',
                    fontSize: '12px',
                    outline: 'none'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  color: '#6b7280',
                  marginBottom: '4px'
                }}>
                  Descrição
                </label>
                <textarea
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    color: '#374151',
                    fontSize: '12px',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  color: '#6b7280',
                  marginBottom: '4px'
                }}>
                  Categoria
                </label>
                <select
                  value={newTemplate.category}
                  onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value as FlowTemplate['category'] })}
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    color: '#374151',
                    fontSize: '12px',
                    outline: 'none'
                  }}
                >
                  <option value="trigger">Gatilho</option>
                  <option value="integration">Integração</option>
                  <option value="data">Dados</option>
                  <option value="notification">Notificação</option>
                  <option value="workflow">Workflow</option>
                  <option value="custom">Personalizado</option>
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  color: '#6b7280',
                  marginBottom: '4px'
                }}>
                  Tags (pressione Enter para adicionar)
                </label>
                <input
                  type="text"
                  placeholder="Digite uma tag e pressione Enter"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const input = e.target as HTMLInputElement;
                      handleAddTag(input.value);
                      input.value = '';
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    color: '#374151',
                    fontSize: '12px',
                    outline: 'none'
                  }}
                />
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  marginTop: '8px'
                }}>
                  {newTemplate.tags?.map(tag => (
                    <span
                      key={tag}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        background: '#ffffff',
                        borderRadius: '4px',
                        fontSize: '11px',
                        color: '#374151'
                      }}
                    >
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#64748b',
                          cursor: 'pointer',
                          padding: 0
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '12px',
                  color: '#6b7280',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={newTemplate.isPublic}
                    onChange={(e) => setNewTemplate({ ...newTemplate, isPublic: e.target.checked })}
                  />
                  Tornar template público
                </label>
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end',
              marginTop: '24px'
            }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewTemplate({
                    name: '',
                    description: '',
                    category: 'workflow',
                    tags: [],
                    isPublic: false
                  });
                }}
                style={{
                  padding: '8px 16px',
                  background: '#6B7280',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateTemplate}
                disabled={!newTemplate.name}
                style={{
                  padding: '8px 16px',
                  background: newTemplate.name ? '#3B82F6' : '#475569',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '12px',
                  cursor: newTemplate.name ? 'pointer' : 'not-allowed'
                }}
              >
                Salvar Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Details Modal */}
      {selectedTemplate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9998
        }}
        onClick={() => setSelectedTemplate(null)}
        >
          <div 
            style={{
              width: '600px',
              maxWidth: '90%',
              background: '#f9fafb',
              borderRadius: '12px',
              padding: '24px',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '20px'
            }}>
              <div>
                <h3 style={{
                  margin: '0 0 8px 0',
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {selectedTemplate.name}
                  {selectedTemplate.isPublic ? <Unlock size={16} color="#10B981" /> : <Lock size={16} color="#6B7280" />}
                  {selectedTemplate.isFavorite && <Heart size={16} color="#EF4444" fill="#EF4444" />}
                </h3>
                <p style={{
                  margin: '0 0 12px 0',
                  fontSize: '12px',
                  color: '#6b7280'
                }}>
                  {selectedTemplate.description}
                </p>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  fontSize: '11px',
                  color: '#64748b'
                }}>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Calendar size={12} />
                    Criado em {new Date(selectedTemplate.createdAt).toLocaleDateString('pt-BR')}
                  </span>
                  {selectedTemplate.author && (
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <User size={12} />
                      {selectedTemplate.author}
                    </span>
                  )}
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <TrendingUp size={12} />
                    {selectedTemplate.usageCount} usos
                  </span>
                </div>
              </div>
              <span style={{
                fontSize: '11px',
                color: '#ffffff',
                background: getCategoryColor(selectedTemplate.category),
                padding: '4px 10px',
                borderRadius: '12px'
              }}>
                {selectedTemplate.category}
              </span>
            </div>

            {/* Tags */}
            {selectedTemplate.tags.length > 0 && (
              <div style={{
                marginBottom: '20px'
              }}>
                <h4 style={{
                  margin: '0 0 8px 0',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6b7280'
                }}>
                  Tags
                </h4>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px'
                }}>
                  {selectedTemplate.tags.map(tag => (
                    <span
                      key={tag}
                      style={{
                        fontSize: '10px',
                        color: '#6b7280',
                        background: '#ffffff',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Tag size={10} />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Dependencies */}
            {selectedTemplate.dependencies && selectedTemplate.dependencies.length > 0 && (
              <div style={{
                marginBottom: '20px'
              }}>
                <h4 style={{
                  margin: '0 0 8px 0',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6b7280'
                }}>
                  Dependências
                </h4>
                <ul style={{
                  margin: 0,
                  paddingLeft: '20px',
                  fontSize: '11px',
                  color: '#64748b'
                }}>
                  {selectedTemplate.dependencies.map(dep => (
                    <li key={dep}>{dep}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end',
              paddingTop: '20px',
              borderTop: '1px solid #d1d5db'
            }}>
              <button
                onClick={() => setSelectedTemplate(null)}
                style={{
                  padding: '8px 16px',
                  background: '#6B7280',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Fechar
              </button>
              {onTemplateExport && (
                <button
                  onClick={() => {
                    onTemplateExport(selectedTemplate);
                    setSelectedTemplate(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    background: '#10B981',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Download size={14} />
                  Exportar
                </button>
              )}
              <button
                onClick={() => {
                  onTemplateSelect?.(selectedTemplate);
                  setSelectedTemplate(null);
                }}
                style={{
                  padding: '8px 16px',
                  background: '#3B82F6',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Check size={14} />
                Usar Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateLibrary;
